import { BeBox, BeScrollView, BeShell, BeTable, type BeTableRow } from "@beos-css/react/index.ts";
import type React from "react";
import { useEffect, useRef, useState } from "react";

import { AppReproductions } from "./demo_sections/app_reproductions";
import { BasicControls } from "./demo_sections/basic_controls";
import { ContainersData } from "./demo_sections/containers_data";
import { Intro } from "./demo_sections/Intro";
import { Layout } from "./demo_sections/layout";
import { NavigationValue } from "./demo_sections/navigation_value";
import { Replicants } from "./demo_sections/replicants";
import { StatusFeedback } from "./demo_sections/status_feedback";
import { Typography } from "./demo_sections/typography";
import { WindowsAlerts } from "./demo_sections/windows_alerts";

type TreeNode = {
	id: string;
	targetId: string;
	level: number;
	parent: string | null;
	cells: Record<string, React.ReactNode>;
	expandable?: boolean;
};

export function App() {
	const [treeNodes, setTreeNodes] = useState<TreeNode[]>([]);
	/* Derived, not listed. This was a literal set of section ids, so a new
	 * section defaulted to COLLAPSED unless someone remembered to add it -- which
	 * is how the Layout section shipped as the only closed one. Every expandable
	 * node starts open; nothing to keep in sync. */
	const [expanded, setExpanded] = useState<Set<string>>(new Set());
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const contentRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!contentRef.current) return;
		const nodes: TreeNode[] = [];
		const headings = contentRef.current.querySelectorAll("h1, h2, h3");

		let lastLevel0: string | null = null;
		let lastLevel1: string | null = null;

		headings.forEach((heading, idx) => {
			const text = heading.textContent || "";
			const section = heading.closest("section");
			const targetId = section?.id || "";

			// Ensure unique ID in case multiple headings share the same section
			let id = targetId;
			if (!id || nodes.some((n) => n.id === id)) {
				id = `${targetId}-${idx}`;
			}

			if (heading.tagName === "H1") {
				lastLevel0 = id;
				nodes.push({ id, targetId, level: 0, parent: null, cells: { label: text }, expandable: false });
			} else if (heading.tagName === "H2") {
				lastLevel1 = id;
				nodes.push({ id, targetId, level: 1, parent: lastLevel0, cells: { label: text }, expandable: false });
			} else if (heading.tagName === "H3") {
				nodes.push({ id, targetId, level: 2, parent: lastLevel1, cells: { label: text }, expandable: false });
			}
		});

		// Mark nodes as expandable if they have children
		nodes.forEach((node) => {
			const hasChildren = nodes.some((n) => n.parent === node.id);
			if (hasChildren) {
				node.expandable = true;
			}
		});

		setTreeNodes(nodes);
		setExpanded(new Set(nodes.filter((n) => n.expandable).map((n) => n.id)));
	}, []);

	const visibleTree: BeTableRow[] = treeNodes
		.filter((n) => {
			let p = n.parent;
			while (p) {
				if (!expanded.has(p)) return false;
				const parentNode = treeNodes.find((x) => x.id === p);
				p = parentNode ? parentNode.parent : null;
			}
			return true;
		})
		.map((n) => ({
			id: n.id,
			cells: n.cells,
			level: n.level,
			expandable: n.expandable,
			expanded: expanded.has(n.id),
		}));

	const toggle = (id: string) =>
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	const handleSelect = (id: string) => {
		setSelectedIds([id]);
		const node = treeNodes.find((n) => n.id === id);
		if (node?.targetId) {
			const el = document.getElementById(node.targetId);
			if (el) {
				el.scrollIntoView({ behavior: "smooth" });
			}
		}
	};

	const sidebar = (
		<BeBox fill flush border="fancy">
			{/* A BeScrollView, not a raw overflow:auto -- that drew a NATIVE
			    scrollbar inside a BeOS UI. `surface="list"` because the tree is a
			    pose view, not chrome. */}
			<BeScrollView fill vertical border="none" surface="list">
				<BeTable
					showHeader={false}
					fillTrailing
					columns={[{ id: "label", title: "Component", width: 250 }]}
					rows={visibleTree}
					selectedIds={selectedIds}
					onSelectItem={handleSelect}
					onToggleExpand={toggle}
					treeLines
				/>
			</BeScrollView>
		</BeBox>
	);

	// at="xlg": the 280 rail plus .demo-content's two 5u gutters must still leave
	// main above `md`, which is 1112 -- so `lg` is 56px short. Under `md` the Layout
	// section's <BeCols> went 16 columns BELOW the threshold and 1 above.
	return (
		<BeShell viewport collapse="hide" at="xlg">
			<BeShell.Rail>{sidebar}</BeShell.Rail>
			{/* 0 is NOT the same as omitting it: omitted, .be-page takes the responsive
			    gutter ladder, and .demo-content's own padding would then double it. */}
			<BeShell.Main pad={0}>
				<div ref={contentRef} className="demo-content">
					{/* A section like any other, so the ladder spaces it: the gap below comes
				    from .demo-content and the gap under the h1 from the section. It was
				    a bare div with marginBottom 64 + 16, which is the per-element margin
				    the rhythm work replaced everywhere else. */}
					<section id="demo-intro-title">
						{/* no inline fontSize: the heading face is a BITMAP strike, native at one ppem
					    per grid. An em-relative size lands off-grid and renders resampled --
					    size comes from --be-font-size-h1 in dpr-bands.css. */}
						<h1>BeOS R5 React Components</h1>
						<p>
							A complete gallery of the BeOS R5 UI toolkit, rebuilt in React and pure CSS. Use the sidebar to jump to specific controls.
						</p>
					</section>
					<Intro />
					<BasicControls />
					<StatusFeedback />
					<WindowsAlerts />
					<ContainersData />
					<Typography />
					<Layout />
					<NavigationValue />
					<AppReproductions />
					<Replicants />
				</div>
			</BeShell.Main>
		</BeShell>
	);
}
